import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import FloorMapSvg from "@/components/FloorMapSvg";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { label: "T?", capacity: 4, x: 50, y: 50, shape: "square" };

/**
 * Per-restaurant floor plan editor (embedded inside AdminRestaurantDetailPage).
 */
export default function FloorSection({ restaurantId }) {
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!restaurantId) return;
        try {
            setLoading(true);
            const { data } = await api.get(`/restaurants/${restaurantId}/tables`);
            setTables(data || []);
        } catch (error) {
            console.warn("[admin-floor] load failed", error?.message);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        load();
    }, [load]);

    const startCreate = () => {
        setEditing(null);
        setForm({ ...EMPTY, label: `T${tables.length + 1}` });
        setOpen(true);
    };
    const startEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); };
    const remove = async (t) => {
        if (!window.confirm(`Delete table ${t.label}?`)) return;
        try {
            await api.delete(`/admin/tables/${t.id}`);
            toast.success("Deleted");
            load();
        } catch (error) {
            console.warn("[admin-floor] delete failed", error?.message);
            toast.error("Failed");
        }
    };
    const save = async () => {
        setSaving(true);
        try {
            if (editing) await api.patch(`/admin/tables/${editing.id}`, form);
            else await api.post("/admin/tables", { ...form, restaurant_id: restaurantId });
            toast.success("Saved");
            setOpen(false);
            load();
        } catch (error) {
            console.warn("[admin-floor] save failed", error?.message);
            toast.error(error?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };
    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    if (loading) return <Skeleton className="h-72" />;

    return (
        <div>
            <div className="flex items-center justify-end mb-3">
                <Button onClick={startCreate} data-testid="admin-floor-create">
                    <Plus className="h-4 w-4 mr-1" /> Add table
                </Button>
            </div>
            <div className="grid lg:grid-cols-[1fr_360px] gap-4">
                <Card><CardContent className="p-4" data-testid="admin-floor-preview">
                    <FloorMapSvg tables={tables} onTableClick={(t) => startEdit(t)} mode="admin" />
                </CardContent></Card>
                <Card><CardContent className="p-4">
                    <h2 className="font-display text-xl mb-3">Tables ({tables.length})</h2>
                    <div className="space-y-1 max-h-[60vh] overflow-y-auto pr-1">
                        {tables.length === 0 ? (
                            <div className="text-sm text-muted-foreground text-center py-6">No tables</div>
                        ) : (
                            tables.map(t => (
                                <div key={t.id} className="flex items-center justify-between p-2 rounded border border-border">
                                    <div>
                                        <div className="font-medium">{t.label}</div>
                                        <div className="text-xs text-muted-foreground">Seats {t.capacity} · {t.shape}</div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => startEdit(t)}><Pencil className="h-4 w-4" /></Button>
                                        <Button size="sm" variant="ghost" onClick={() => remove(t)} className="text-[hsl(var(--destructive))]"><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </CardContent></Card>
            </div>

            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="admin-floor-editor">
                    <SheetHeader><SheetTitle>{editing ? "Edit" : "New"} table</SheetTitle></SheetHeader>
                    <div className="space-y-3 mt-4">
                        <FormRow label="Label"><Input value={form.label} onChange={(e) => update("label", e.target.value)} data-testid="admin-floor-form-label" /></FormRow>
                        <FormRow label={`Capacity (${form.capacity})`}>
                            <Slider min={1} max={12} step={1} value={[form.capacity]} onValueChange={(v) => update("capacity", v[0])} />
                        </FormRow>
                        <FormRow label="Shape">
                            <Select value={form.shape} onValueChange={(v) => update("shape", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="square">Square</SelectItem>
                                    <SelectItem value="circle">Circle</SelectItem>
                                    <SelectItem value="rect">Rect (booth)</SelectItem>
                                </SelectContent>
                            </Select>
                        </FormRow>
                        <FormRow label={`Position X (${form.x.toFixed(0)})`}>
                            <Slider min={5} max={95} step={1} value={[form.x]} onValueChange={(v) => update("x", v[0])} />
                        </FormRow>
                        <FormRow label={`Position Y (${form.y.toFixed(0)})`}>
                            <Slider min={5} max={95} step={1} value={[form.y]} onValueChange={(v) => update("y", v[0])} />
                        </FormRow>
                        <div className="mt-4">
                            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preview</Label>
                            <FloorMapSvg tables={[...tables.filter(t => t.id !== editing?.id), { ...form, id: "preview" }]} selectedTableId="preview" />
                        </div>
                    </div>
                    <SheetFooter className="mt-6">
                        <Button onClick={save} disabled={saving} data-testid="admin-floor-save">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save</Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>
        </div>
    );
}

function FormRow({ label, children }) {
    return <div className="grid gap-1"><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>;
}
